import type { OptJobReview } from 'api/optimizations/optimizations.serializers'
import type { OptJobStatus, OptJobType } from 'api/optJobs/optJobs.api'
import { ACTIVE_OPT_JOB_STATUSES, getOptjobAsObject, useLatestOptJobStatus } from 'api/optJobs/optJobs.api'
import { buildSubtypeAliases, DISABLED_SUBTYPE_ALIAS_VALUE } from 'helpers/optimize.helper/optimize.helper'
import { useEffect, useMemo, useState } from 'react'
import Datetime from 'utils/datetime/datetime'
import { isValidIntradayPriceValue } from 'views/IntradayTrading/priceValidation.helpers'
import type { HourInput, IntradayPrices } from './IntradayTradingView.types'

export const INTRADAY_TRADING_VIEW_ANCHOR = 'intraday_trading_view'
export const INTRADAY_OVERVIEW_ANCHOR = 'intraday_overview'

export { DISABLED_SUBTYPE_ALIAS_VALUE }

export function buildIntradayResultAliases({
  subtypes,
  activeSubtype,
  activeSubtypeDisplayName,
}: {
  subtypes: Subtype[]
  activeSubtype: string
  activeSubtypeDisplayName: string | null
}): UiConfigAliases {
  // Design rationale:
  // We intentionally expose both the new generic alias contract (selected_subtype*) and
  // legacy intraday aliases to keep existing customer UiConfigs working while enabling a
  // gradual migration to subtype-agnostic naming. A hard cutover would require synchronized
  // backend/config rollout across systems and create avoidable operational risk.
  //
  // We also generate subtype_<value> aliases from runtime subtype data instead of hardcoding
  // customer-specific keys (such as cs/nv) in application code. This keeps the frontend reusable
  // across systems that use different subtype names, with the convention isolated to the alias
  // contract in UiConfig rather than in view logic.
  const genericAliases = buildSubtypeAliases({
    options: subtypes.map((subtype) => ({
      key: subtype.subtype,
      value: subtype.subtype,
      displayName: subtype.subtype_display_name,
    })),
    selectedKey: activeSubtype,
  })

  return {
    ...genericAliases,
    selected_subtype: activeSubtype || null,
    selected_subtype_display_name: activeSubtypeDisplayName,
    intraday_subtype: activeSubtype || null,
    intraday_subtype_display_name: activeSubtypeDisplayName,
  }
}

type LatestIntradayOptJobData = {
  id: number | null
  timeSince: string | null
  status: OptJobStatus | null
  startedAt: string | null
  isLoading: boolean
  isError: boolean
}

export function useLatestIntradayOptJobData(
  optJobType: OptJobType,
  status?: OptJobStatus,
  subtypes: string[] = []
): LatestIntradayOptJobData {
  const { data, isLoading, isError } = useLatestOptJobStatus({ type: optJobType, status, limit: 1, subtypes })
  const optJob = useMemo(() => getOptjobAsObject(data), [data])
  const latestStatus = useMemo(() => optJob?.status ?? null, [optJob])
  const latestStartedAt = useMemo(() => {
    if (!optJob?.created_at) {
      return null
    }

    return Datetime.toLocalTime(Datetime.toISOString(optJob.created_at), 'longDayTextWithoutYear')
  }, [optJob])

  const optJobId = useMemo(() => optJob?.id ?? null, [optJob])
  const updatedAt = useMemo(() => optJob?.updated_at ?? null, [optJob])
  const [timeSince, setTimeSince] = useState<string | null>(null)

  useEffect(() => {
    const calculate = () => {
      if (!updatedAt) {
        setTimeSince(null)
        return
      }

      setTimeSince(
        Datetime.getDurationInHoursAndMinutes(
          Datetime.toISOString(updatedAt),
          Datetime.toISOString(Datetime.getISONow(0, false))
        )
      )
    }

    calculate()
    const interval = setInterval(calculate, 60000)

    return () => clearInterval(interval)
  }, [updatedAt])

  return {
    id: optJobId,
    timeSince,
    status: latestStatus,
    startedAt: latestStartedAt,
    isLoading,
    isError,
  }
}

export function isIntradayOptJobActive(status: OptJobStatus | null): boolean {
  return status !== null && status !== undefined && ACTIVE_OPT_JOB_STATUSES.has(status)
}

export function isValidPriceValue(value: string): boolean {
  return isValidIntradayPriceValue(value)
}

export function hasAllPriceValues(prices: HourInput[]): boolean {
  const enteredPrices = prices.filter((price) => price.value.trim() !== '')

  return enteredPrices.length > 0 && enteredPrices.every((price) => isValidPriceValue(price.value))
}

export function getNextPriceRowIndexFromKeyboardInput({
  key,
  currentRowIndex,
  rowCount,
}: {
  key: string
  currentRowIndex: number
  rowCount: number
}): number | null {
  if (rowCount <= 0) {
    return null
  }

  const delta = key === 'ArrowUp' ? -1 : key === 'ArrowDown' || key === 'Enter' ? 1 : 0

  if (delta === 0) {
    return null
  }

  const targetRowIndex = currentRowIndex + delta

  if (targetRowIndex < 0 || targetRowIndex >= rowCount) {
    return currentRowIndex
  }

  return targetRowIndex
}

function isValidIntradayPriceTime(time: unknown): time is IntradayPrices['times'][number] {
  return typeof time === 'string' || typeof time === 'number'
}

function isValidIntradayReviewPriceValue(value: unknown): value is IntradayPrices['values'][number] {
  return value === null || typeof value === 'number'
}

export function getIntradayPricesFromReview(review: OptJobReview | undefined): IntradayPrices | undefined {
  const prices = review?.input_data?.intraday_prices
  if (!prices || !Array.isArray(prices.times) || !Array.isArray(prices.values)) {
    return undefined
  }

  if (!prices.times.every(isValidIntradayPriceTime) || !prices.values.every(isValidIntradayReviewPriceValue)) {
    return undefined
  }

  if (prices.values.length < prices.times.length) {
    return undefined
  }

  return { times: prices.times, values: prices.values }
}
